import React, { useEffect, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Plus, Mail, Trash2, X, RefreshCw, Check, Settings2, UserCheck, UserMinus } from 'lucide-react';
import { api } from './api';
import AvailabilityEditor from './AvailabilityEditor';

const STATUS_LABELS = { planifie: 'Planifié', confirme: 'Confirmé', realise: 'Réalisé', annule: 'Annulé' };
const STATUS_TONE = {
  planifie: 'bg-amber-100 text-amber-700',
  confirme: 'bg-emerald-100 text-emerald-700',
  realise: 'bg-sky-100 text-sky-700',
  annule: 'bg-slate-200 text-slate-500',
};
const WEEKDAYS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const toLocalInput = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };

const EMPTY = { nom: '', email: '', date_heure: '', lien_visio: '', statut: 'planifie', notes: '' };

export default function AppointmentsTab({ prefill, onPrefillConsumed, onGoToRequest, currentAdmin }) {
  const [appointments, setAppointments] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [modal, setModal] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [showAvailability, setShowAvailability] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api('/appointments');
      setAppointments(data.appointments);
      setStatuses(data.statuses);
    } catch (e) { /* noop */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (prefill) {
      setModal({ mode: 'create', data: { ...EMPTY, ...prefill, date_heure: toLocalInput(null) } });
      onPrefillConsumed && onPrefillConsumed();
    }
  }, [prefill, onPrefillConsumed]);

  const monthAppts = appointments.filter((a) => {
    const d = new Date(a.date_heure);
    return d.getFullYear() === cursor.y && d.getMonth() === cursor.m;
  }).sort((a, b) => new Date(a.date_heure) - new Date(b.date_heure));

  const countByDay = {};
  monthAppts.forEach((a) => { const day = new Date(a.date_heure).getDate(); countByDay[day] = (countByDay[day] || 0) + 1; });

  const firstDay = (new Date(cursor.y, cursor.m, 1).getDay() + 6) % 7; // lundi=0
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();

  const shiftMonth = (delta) => {
    setCursor((c) => { const d = new Date(c.y, c.m + delta, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
  };

  const openCreate = (day) => {
    const base = new Date(cursor.y, cursor.m, day || 1, 10, 0);
    setModal({ mode: 'create', data: { ...EMPTY, date_heure: toLocalInput(base.toISOString()) } });
  };

  const save = async (data) => {
    const payload = { ...data, date_heure: new Date(data.date_heure).toISOString() };
    if (modal.mode === 'create') await api('/appointments', { method: 'POST', body: payload });
    else await api(`/appointments/${data.id}`, { method: 'PATCH', body: payload });
    setModal(null);
    load();
  };

  const remove = async (id) => {
    await api(`/appointments/${id}`, { method: 'DELETE' });
    load();
  };

  const sendConfirmation = async (a) => {
    setSendingId(a.id);
    try {
      const res = await api(`/appointments/${a.id}/send-confirmation`, { method: 'POST' });
      if (res.success) load();
      else alert("L'e-mail n'a pas pu être envoyé (SMTP).");
    } catch (e) { alert(e.message); } finally { setSendingId(null); }
  };

  const assign = async (apptId) => {
    try {
      await api(`/appointments/${apptId}/assign`, { method: 'POST' });
      load();
    } catch (e) { alert(e.message); }
  };

  const unassign = async (apptId) => {
    try {
      await api(`/appointments/${apptId}/assign`, { method: 'DELETE' });
      load();
    } catch (e) { alert(e.message); }
  };

  return (
    <div className="grid lg:grid-cols-[320px_1fr] gap-5" data-testid="papa-appointments-tab">
      {/* Calendrier léger */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 h-fit">
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => shiftMonth(-1)} data-testid="papa-cal-prev" className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600"><ChevronLeft size={18} /></button>
          <span className="font-semibold text-slate-900 text-sm">{MONTHS[cursor.m]} {cursor.y}</span>
          <button onClick={() => shiftMonth(1)} data-testid="papa-cal-next" className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600"><ChevronRight size={18} /></button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center">
          {WEEKDAYS.map((d, i) => <span key={i} className="text-[10px] font-bold text-slate-400 py-1">{d}</span>)}
          {Array.from({ length: firstDay }).map((_, i) => <span key={`e${i}`} />)}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const has = countByDay[day];
            return (
              <button key={day} onClick={() => openCreate(day)}
                className={`relative aspect-square rounded-lg text-xs flex items-center justify-center hover:bg-sky-50 transition-colors ${has ? 'bg-sky-100 text-sky-800 font-bold' : 'text-slate-600'}`}>
                {day}
                {has ? <span className="absolute bottom-1 w-1.5 h-1.5 rounded-full bg-sky-500" /> : null}
              </button>
            );
          })}
        </div>
        <button onClick={() => openCreate(1)} data-testid="papa-appt-new"
          className="mt-4 w-full flex items-center justify-center gap-2 bg-slate-900 text-white text-sm font-semibold py-2.5 rounded-lg hover:bg-slate-800">
          <Plus size={16} /> Nouveau rendez-vous
        </button>
      </div>

      {/* Liste du mois */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-slate-500">{monthAppts.length} rendez-vous — {MONTHS[cursor.m]} {cursor.y}</p>
          <button onClick={load} className="flex items-center gap-1.5 text-xs text-slate-600"><RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Actualiser</button>
        </div>
        <div className="space-y-2">
          {monthAppts.map((a) => (
              <div
              key={a.id}
              className="bg-white rounded-xl border border-slate-200 p-4 cursor-pointer hover:border-slate-400 hover:shadow-sm transition-all"
              data-testid="papa-appt-row"
              onClick={() => onGoToRequest?.(a)}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-900">{a.nom}</span>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_TONE[a.statut]}`}>{STATUS_LABELS[a.statut] || a.statut}</span>
                    {a.confirmation_sent_at && <span className="text-[10px] text-emerald-600 flex items-center gap-0.5"><Check size={12} /> confirmé</span>}
                    {/* Badge attribution */}
                    {a.assigned_admin_name
                      ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 flex items-center gap-1"><UserCheck size={11} /> {a.assigned_admin_name}</span>
                      : <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200">Non attribué</span>
                    }
                  </div>
                  <p className="text-sm text-slate-600 mt-1">
                    {new Date(a.date_heure).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} à {fmtTime(a.date_heure)}
                  </p>
                  <p className="text-sm text-slate-500">{a.email}</p>
                  {a.lien_visio && (
                    <span className="text-xs text-sky-600 break-all">{a.lien_visio}</span>
                  )}
                  {a.notes && <p className="text-sm text-slate-500 mt-1 italic">{a.notes}</p>}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap" onClick={(e) => e.stopPropagation()}>
                  {/* Bouton d'attribution */}
                  {!a.assigned_admin_id && (
                    <button
                      onClick={() => assign(a.id)}
                      data-testid="papa-appt-assign"
                      className="flex items-center gap-1 text-xs font-semibold text-indigo-700 border border-indigo-200 bg-indigo-50 rounded-lg px-2.5 py-1.5 hover:bg-indigo-100"
                    >
                      <UserCheck size={14} /> Me l'attribuer
                    </button>
                  )}
                  {a.assigned_admin_id === currentAdmin?.id && (
                    <button
                      onClick={() => unassign(a.id)}
                      data-testid="papa-appt-unassign"
                      className="flex items-center gap-1 text-xs font-semibold text-slate-600 border border-slate-200 bg-slate-50 rounded-lg px-2.5 py-1.5 hover:bg-slate-100"
                    >
                      <UserMinus size={14} /> Se retirer
                    </button>
                  )}
                  <button onClick={() => sendConfirmation(a)} disabled={sendingId === a.id} data-testid="papa-appt-send"
                    className="flex items-center gap-1 text-xs font-semibold text-sky-700 border border-sky-200 bg-sky-50 rounded-lg px-2.5 py-1.5 hover:bg-sky-100 disabled:opacity-50">
                    <Mail size={14} /> {sendingId === a.id ? 'Envoi…' : 'Confirmation'}
                  </button>
                  <button onClick={() => setModal({ mode: 'edit', data: { ...a, date_heure: toLocalInput(a.date_heure) } })} data-testid="papa-appt-edit"
                    className="text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg px-2.5 py-1.5 hover:bg-slate-50">Modifier</button>
                  <button onClick={() => remove(a.id)} data-testid="papa-appt-delete"
                    className="w-8 h-8 flex items-center justify-center text-red-500 border border-red-200 rounded-lg hover:bg-red-50"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
          {!loading && monthAppts.length === 0 && <p className="text-center text-sm text-slate-400 py-12">Aucun rendez-vous ce mois-ci.</p>}
        </div>
      </div>

      {modal && <AppointmentModal modal={modal} statuses={statuses} onClose={() => setModal(null)} onSave={save} />}

      {/* ── Section disponibilités ── */}
      <div className="mt-6 border-t border-slate-200 pt-6">
        <button
          onClick={() => setShowAvailability((v) => !v)}
          data-testid="toggle-availability"
          className="flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-4 transition-colors"
        >
          <Settings2 size={16} />
          {showAvailability ? 'Masquer les disponibilités' : 'Gérer les disponibilités pour la prise de RDV en ligne'}
          <span className="text-slate-300">{showAvailability ? '▲' : '▼'}</span>
        </button>
        {showAvailability && <AvailabilityEditor />}
      </div>
    </div>
  );
}

function AppointmentModal({ modal, statuses, onClose, onSave }) {
  const [form, setForm] = useState(modal.data);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setSaving(true);
    try { await onSave(form); } catch (ex) { setErr(ex.message); setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="papa-appt-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg text-slate-900">{modal.mode === 'create' ? 'Nouveau rendez-vous' : 'Modifier le rendez-vous'}</h3>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center"><X size={18} /></button>
        </div>
        {err && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</p>}
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="block text-xs font-semibold text-slate-600 mb-1">Nom du prospect</label>
            <input required value={form.nom} onChange={(e) => set('nom', e.target.value)} data-testid="papa-appt-nom" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-semibold text-slate-600 mb-1">E-mail</label>
            <input required type="email" value={form.email} onChange={(e) => set('email', e.target.value)} data-testid="papa-appt-email" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Date & heure</label>
            <input required type="datetime-local" value={form.date_heure} onChange={(e) => set('date_heure', e.target.value)} data-testid="papa-appt-datetime" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Statut</label>
            <select value={form.statut} onChange={(e) => set('statut', e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm">
              {statuses.map((s) => <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-semibold text-slate-600 mb-1">Lien visio (optionnel)</label>
            <input value={form.lien_visio || ''} onChange={(e) => set('lien_visio', e.target.value)} placeholder="https://meet…" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-semibold text-slate-600 mb-1">Notes (optionnel)</label>
            <textarea value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} rows={2} className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm resize-none" />
          </div>
        </div>
        <button type="submit" disabled={saving} data-testid="papa-appt-save" className="w-full bg-slate-900 text-white font-semibold text-sm py-2.5 rounded-lg hover:bg-slate-800 disabled:opacity-60">
          {saving ? 'Envoi en cours…' : 'Créer le RDV & Envoyer la confirmation'}
        </button>
      </form>
    </div>
  );
}
